/****************************************************
 * Module Name:  LockedPlanning.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useMemo, useEffect } from "react";
import { Box, TextField, Tooltip, Typography, Stack, Button } from "@mui/material";
import Constants from "../../../utility/constants";
import NoPermission from "../../common/noPermission";
import Utility from "../../../utility/utility";
import { LoadingButton } from "@mui/lab";
import PricePlanningProxy from "../../../utility/proxy/manager/manufacturingProxy/pricePlanningProxy";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { BiSearchAlt2 } from "react-icons/bi";
import TabMenuSnackbar from "../../common/tabMenuSnackbar";
import renderCellExpand from "../../common/dataGridCellExpand";

import PricePlanDetailModal from "./pricePlanDetailModal";
import KapanHistoryModal from "./kapanHistoryModal";
import PacketHistoryModal from "../../common/packetHistoryModal";
import PageTitle from "../../common/pageTitle";

const LockedPlanning = () => {

    const [kapanId, setKapanId] = useState('');
    const [packetId, setPacketId] = useState('');
    const [lockedPlanningLoader, setLockedPlanningLoader] = useState(false);
    const [rows, setRows] = useState([]);
    const [filterValues, setFilterValues] = useState({});

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    const [showPricePlanDetailModal, setShowPricePlanDetailModal] = useState(false);
    const [stoneId, setStoneId] = useState("");
    const [selectedRow, setSelectedRow] = useState({});

    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);

    const [lockedPricePlanningPermission] = React.useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOCKEDPLANNING ||
                iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOCKEDPLANNING_MARKER
        )
    );

    useEffect(() => {
        PageTitle("MANUFACTURING_OPERATION_PRICEPLANNING");
    }, []);

    const isChecker = React.useMemo(() => {

        return lockedPricePlanningPermission &&
            lockedPricePlanningPermission[0].permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOCKEDPLANNING_MARKER
            ? false
            : lockedPricePlanningPermission &&
                lockedPricePlanningPermission[0].permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOCKEDPLANNING
                ? true
                : null;

    }, [lockedPricePlanningPermission]);

    const handleSearch = () => {
        setLockedPlanningLoader(true);
        let data = {
            userid: Constants.loggedInUser.id,
            kapanid: parseInt(kapanId),
            packetid: packetId,
            status: 'LOCKED',
            date: null,
            ischecker: isChecker,
        };
        PricePlanningProxy.getAllPricePlanningByFilters(data, (responseData) => {
            if (responseData) {
                const transformedData = responseData.map(item => ({
                    ...item,
                    priceplanningdetail: item.priceplanningdetail.map(detail => ({
                        ...detail,
                        stonetype: detail.stonetype === "L" ? "Lab Grown Diamonds" : "Natural Diamonds",
                    })),
                }));

                // Flatten and process the details for filter values
                const response = transformedData.flatMap(item => item.priceplanningdetail);
                const uniqueFilterValues = {
                    createdbyusername: Utility.getDistinctOptions(response, "createdbyusername"),
                    shape: Utility.getDistinctOptions(response, "shape"),
                    color: Utility.getDistinctOptions(response, "color"),
                    cut: Utility.getDistinctOptions(response, "cut"),
                    pol: Utility.getDistinctOptions(response, "pol"),
                    sym: Utility.getDistinctOptions(response, "sym"),
                    clarity: Utility.getDistinctOptions(response, "clarity"),
                    flo: Utility.getDistinctOptions(response, "flo"),
                    stonetype: Utility.getDistinctOptions(response, "stonetype"),
                    lockedbyusername: Utility.getDistinctOptions(response, "lockedbyusername"),
                    status: Utility.getDistinctOptions(response, "status"),
                    lab: Utility.getDistinctOptions(response, "lab"),
                };

                setFilterValues(uniqueFilterValues);
                setLockedPlanningLoader(false);
                setRows(transformedData);
            } else {
                setLockedPlanningLoader(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    }

    const initialColumns = () => [
        {
            field: 'kapanid',
            headerName: 'KAPANID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
            ),
            width: 80
        },
        {
            field: 'packetid',
            headerName: 'PACKETID',
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => {
                        setPacketId(params.value);
                        setKapanId(params.row.kapanid);
                        setShowPacketDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
            ),
            width: 100
        },
        {
            field: 'planid',
            headerName: 'PLAN ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalcount',
            headerName: 'COUNT',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalsize',
            headerName: 'TOTAL SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalstoneprice',
            headerName: 'TOTAL STONE PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalprice',
            headerName: 'TOTAL PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },

    ];

    const middleColumns = useMemo(() => [
        {
            field: 'id',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'kapanid',
            headerName: 'KAPANID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'packetid',
            headerName: 'PACKETID',
            headerAlign: 'center',
            align: 'center',
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: 'shape',
            headerName: 'SHAPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'size',
            headerName: 'SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'color',
            headerName: 'COLOR',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 65
        },
        {
            field: 'clarity',
            headerName: 'CLARITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 75
        },
        {
            field: 'cut',
            headerName: 'CUT',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'pol',
            headerName: 'POL',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'sym',
            headerName: 'SYM',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'flo',
            headerName: 'FLO',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'lab',
            headerName: 'LAB',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lab : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'stonetype',
            headerName: 'STONE TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueGetter: (params) => params.value,
            valueOptions: filterValues ? filterValues.stonetype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value,
            width: 150
        },
        {
            field: 'rapnetprice',
            headerName: 'RAP',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'ppc',
            headerName: 'PPC',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80
        },
        {
            field: 'discount',
            headerName: 'DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 80,
            editable: true,
            valueFormatter: (params) => params.value,
        },
        {
            field: 'labprice',
            headerName: 'LAB PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'total',
            headerName: 'TOTAL',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80
        },
        {
            field: 'stoneprice',
            headerName: 'STONE PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 120
        },
        {
            field: 'view',
            headerName: 'VIEW',
            headerAlign: 'center',
            align: 'center',
            width: 100,
            renderCell: (params) => (
                <button
                    style={{
                        padding: '5px 10px',
                        cursor: 'pointer',
                        backgroundColor: '#000000',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                    }}
                    onClick={() => handleViewButtonClick(params.row)}>
                    VIEW
                </button>
            ),
        },
        {
            field: 'iscertified',
            headerName: 'IS CERTIFIED',
            headerAlign: 'center',
            align: 'center',
            type: "boolean",
            width: 100
        },
        {
            field: 'createdby',
            headerName: 'CREATED BY ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'createdbyusername',
            headerName: 'CREATED BY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.createdbyusername : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'lockedby',
            headerName: 'LOCKED BY ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'lockedbyusername',
            headerName: 'LOCKED BY',
            headerAlign: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lockedbyusername : [],
            filterOperators: Utility.combinedFilters(),
            align: 'center',
            width: 100
        },
        {
            field: 'createdat',
            headerName: 'CREATED AT',
            headerAlign: 'center',
            align: 'center',
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            },
            width: 150
        },
        {
            field: 'updatedat',
            headerName: 'UPDATED AT',
            headerAlign: 'center',
            align: 'center',
            width: 110,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        {
            field: 'status',
            headerName: 'STATUS',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.status : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'rapnetdate',
            headerName: 'RAPNET DATE',
            headerAlign: 'center',
            align: 'center',
            width: 100,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
    ], [filterValues]);

    const handleViewButtonClick = (selectedRow) => {
        setSelectedRow(selectedRow)
        setStoneId(selectedRow.id);
        setShowPricePlanDetailModal(true);
    }

    const columns = initialColumns();

    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback((row) => <DetailPanelContent row={row} />,
        [middleColumns],);

    function DetailPanelContent({ row: rowProp }) {

        let rows = rowProp.priceplanningdetail;

        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "90vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: 'auto',
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={middleColumns}
                        rows={rows}
                        getRowId={(row) => row.id}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            maxHeight: '30vh',
                        }}
                        // loading={isLoading}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    id: false,
                                    createdby: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    createdat: false,
                                    kapanid: false,
                                    packetid: false,
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                    />
                </Box>
            </Stack>
        );
    }

    return (
        <Box>
            {
                !Utility.isReadAllowed(lockedPricePlanningPermission) ? <NoPermission /> : (
                    <Box px={1}>
                        <TabMenuSnackbar
                            show={showSnackbar}
                            message={snackbarMessage}
                            variant={snackbarVariant}
                            autoHide={Constants.SnackBarCloseTimer}
                            title={snackbarVariant === "success" ? "Success" :
                                (snackbarVariant === "error" ? "Failed" : "Warning")}
                            handleClose={() => setShowSnackbar(false)}
                        />
                        {showKapanDetailModal ? (
                            <KapanHistoryModal
                                show={showKapanDetailModal}
                                kapanId={kapanId}
                                close={() => setShowKapanDetailModal(false)}
                            />
                        ) : null}

                        {showPacketDetailModal ? (
                            <PacketHistoryModal
                                show={showPacketDetailModal}
                                packetId={packetId}
                                kapanId={kapanId}
                                close={() => setShowPacketDetailModal(false)}
                            />
                        ) : null}
                        {showPricePlanDetailModal ? (
                            <PricePlanDetailModal
                                show={showPricePlanDetailModal}
                                stoneId={stoneId}
                                kapanid={selectedRow.kapanid}
                                packetid={selectedRow.packetid}
                                handleClose={() => setShowPricePlanDetailModal(false)}
                            />
                        ) : null}
                        <>
                            <Box sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: "30px",
                                width: "50%",
                                paddingTop: "13px",
                                "& .MuiOutlinedInput-input": {
                                    padding: "9px !important",
                                    height: "25px !important"
                                },
                                "& .MuiInputLabel-root": {
                                    top: "-7px !important"
                                },
                                "& .MuiOutlinedInput-root": {
                                    padding: 0
                                }
                            }}>
                                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                    <Typography sx={{ width: "20%", mr: 2 }}>Kapan Id</Typography>
                                    <TextField
                                        sx={{ width: "80%", margin: 0 }}
                                        margin="normal"
                                        label="Kapan Id"
                                        placeholder="Enter Kapan Id"
                                        value={kapanId}
                                        onChange={(e) => {
                                            if (/^[1-9]\d*$/.test(e.target.value) || e.target.value === "") {
                                                setKapanId(e.target.value);
                                            }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                    <Typography sx={{ width: "20%", mr: 2 }}>Packet Id</Typography>
                                    <TextField
                                        sx={{ width: "80%", margin: 0 }}
                                        margin="normal"
                                        label="Packet Id"
                                        placeholder="Enter Packet Id"
                                        multiline
                                        value={packetId}
                                        onChange={(e) => setPacketId(Utility.ConvertNewLineToComma(e.target.value))}
                                    />
                                </Box>
                                <Box sx={{
                                    "& .Mui-disabled": {
                                        backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                        color: "rgba(0, 0, 0, 0.26) !important"
                                    }
                                }}>
                                    <LoadingButton
                                        sx={{ mr: 1 }}
                                        variant="contained"
                                        color="info"
                                        onClick={() => handleSearch()}
                                        loading={lockedPlanningLoader}
                                        loadingPosition="start"
                                        startIcon={<BiSearchAlt2 />}
                                    >
                                        Search
                                    </LoadingButton>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    width: "100%",
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    position: "relative",
                                    paddingTop: "20px",
                                    "& .MuiTablePagination-selectLabel": {
                                        marginTop: "15px",
                                    },
                                    "& .MuiTablePagination-displayedRows": {
                                        marginTop: "15px",
                                    },
                                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .css-cc8tf1": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    height: "71vh",
                                }}
                            >
                                <DataGridPro
                                    disableRowSelectionOnClick={true}
                                    sx={{
                                        '& .MuiDataGrid-columnHeader': {
                                            color: '#fff',
                                            backgroundColor: 'grey',
                                        },
                                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                            py: "4px",
                                        },
                                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                            py: "12px",
                                        },
                                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                            py: "18px",
                                        },

                                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                    }}
                                    density="compact"
                                    loading={lockedPlanningLoader}
                                    slots={{ toolbar: GridToolbar }}
                                    rows={rows}
                                    columns={columns}
                                    getRowId={(row) => row.embededkey}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: {
                                                // Hide columns id, the other columns will remain visible
                                                id: false,
                                                createdby: false,
                                                createdbyusername: false,
                                                lockedby: false,
                                                status: false,
                                                rapnetdate: false,
                                                createdat: false
                                            },
                                        },
                                        pagination: {
                                            paginationModel: Constants.pageSize,
                                        },
                                        sorting: {
                                            sortModel: [{ field: "updatedat", sort: "desc" }],
                                        },
                                    }}
                                    pageSizeOptions={Constants.pageSizeOptions}
                                    pagination
                                    slotProps={{
                                        toolbar: {
                                            showQuickFilter: true,
                                            quickFilterProps: { debounceMs: 500 },
                                        },
                                    }}
                                    getDetailPanelHeight={getDetailPanelHeight}
                                    getDetailPanelContent={({ row }) => getDetailPanelContent(row)}
                                />
                                <Box
                                    style={{
                                        position: "absolute",
                                        top: "32px",
                                        right: "265px",
                                        display: "flex",
                                        gap: "15px",
                                    }}
                                >
                                    {lockedPlanningLoader ? (
                                        <CircularProgress size="1rem" />
                                    ) : (
                                        <Tooltip title="Reload List">
                                            <RefreshOutlinedIcon
                                                onClick={() =>
                                                    handleSearch()
                                                }
                                                color="primary"
                                                fontSize="medium"
                                                sx={{ cursor: "pointer" }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box></>
                    </Box>
                )}
        </Box>
    );
};

export default LockedPlanning;